import { useEffect, useState } from 'react';

import { AxiosError } from 'axios';

import { apiStrapi } from 'services/apiService';

// Simple in-memory cache
let cachedWhatsappNumber: string | null | undefined = undefined;
let cachedWhatsappText: string | null | undefined = undefined;
let cachedError: Error | null = null;
let cachedStatus: 'idle' | 'loading' | 'done' = 'idle';

const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // If the number starts with a plus, keep it
  const hasPlus = phone.startsWith('+');

  // Format the number with spaces and dashes
  // This will work for any country code and number length
  const formatted = digits.replace(/(\d{2})(\d{2})(\d{4,5})(\d{4})/, '$1 $2 $3-$4');
  const result = hasPlus ? `+${formatted}` : formatted;
  return result;
};

export const useDynamicWhatsapp = () => {
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(cachedWhatsappNumber ?? null);
  const [whatsappText, setWhatsappText] = useState<string | null>(cachedWhatsappText ?? null);
  const [isLoading, setIsLoading] = useState(cachedStatus === 'loading');
  const [error, setError] = useState<Error | null>(cachedError);

  useEffect(() => {
    let isMounted = true;
    if (cachedStatus === 'done') {
      return;
    }
    if (cachedStatus === 'loading') {
      return;
    }

    const loadWhatsappData = async () => {
      cachedStatus = 'loading';
      setIsLoading(true);
      try {
        const response = await apiStrapi.get('/organizations/support-contact');
        const data = response.data;

        if (data && data.phone) {
          const formattedNumber = formatPhoneNumber(data.phone);
          cachedWhatsappNumber = formattedNumber;
          cachedWhatsappText = data.text || '';
          cachedStatus = 'done';

          if (isMounted) {
            setWhatsappNumber(formattedNumber);
            setWhatsappText(data.text || '');
          }
          return;
        } else {
          console.warn(
            '[useDynamicWhatsapp] Dados do WhatsApp não encontrados ou formato inesperado:',
            response.data
          );
          cachedWhatsappNumber = null;
          cachedWhatsappText = null;
          cachedStatus = 'done';

          if (isMounted) {
            setWhatsappNumber(null);
            setWhatsappText(null);
          }
        }
      } catch (apiError) {
        console.error('[useDynamicWhatsapp] Error loading WhatsApp data:', apiError);
        const axiosError = apiError as AxiosError;
        if (axiosError.response?.status === 404) {
          cachedWhatsappNumber = null;
          cachedWhatsappText = null;
          cachedStatus = 'done';

          if (isMounted) {
            setWhatsappNumber(null);
            setWhatsappText(null);
          }
        } else {
          cachedError = apiError as Error;
          cachedStatus = 'done';
          if (isMounted) setError(apiError as Error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadWhatsappData();
    return () => {
      isMounted = false;
    };
  }, []);

  return { whatsappNumber, whatsappText, isLoading, error };
};
